// File: src/components/CookieConsentBanner.jsx
import React, { useEffect, useState } from 'react';

const COOKIE_NAME = 'cookie_consent';
const USE_CUSTOM_CMP = false; // Set to true to enable this custom CMP again

const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'UK'
];

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    ads: false,
  });

  useEffect(() => {
    if (!USE_CUSTOM_CMP) return;

    const saved = localStorage.getItem(COOKIE_NAME);
    if (saved) {
      const parsed = JSON.parse(saved);
      applyConsent(parsed);
    } else {
      fetch('https://get.geojs.io/v1/ip/country.json')
        .then(res => res.json())
        .then(data => {
          if (EU_COUNTRIES.includes(data.country)) {
            setVisible(true);
          }
        })
        .catch(() => {
          // Show banner if geo lookup fails
          setVisible(true);
        });
    }
  }, []);

  const applyConsent = (prefs) => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: prefs.ads ? 'granted' : 'denied',
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_user_data: prefs.ads ? 'granted' : 'denied',
        ad_personalization: prefs.ads ? 'granted' : 'denied',
        wait_for_update: 500
      });
    }
    if (!prefs.analytics) {
      window['ga-disable-G-XXXXXXXXXX'] = true; // Replace with your GA4 ID
    }
  };

  const savePreferences = (prefs) => {
    localStorage.setItem(COOKIE_NAME, JSON.stringify(prefs));
    setVisible(false);
    applyConsent(prefs);
  };

  const acceptAll = () => {
    const allPrefs = { necessary: true, analytics: true, ads: true };
    setPreferences(allPrefs);
    savePreferences(allPrefs);
  };

  const rejectAll = () => {
    const essentialOnly = { necessary: true, analytics: false, ads: false };
    setPreferences(essentialOnly);
    savePreferences(essentialOnly);
  };

  if (!USE_CUSTOM_CMP || !visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 bg-gray-900 text-white p-4 z-50 text-sm shadow-lg">
      {!showSettings ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            We use cookies to personalize ads and analyze traffic. Your support helps keep this site free.
            Read our <a href="/privacy-policy" className="underline text-blue-300">Privacy Policy</a>.
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <button className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded" onClick={acceptAll}>Accept Recommended</button>
            <button className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded" onClick={rejectAll}>Essential Only</button>
            <button className="underline text-blue-300" onClick={() => setShowSettings(true)}>Customize</button>
          </div>
        </div>
      ) : (
        <div>
          <div className="mb-2 font-semibold">Customize Cookie Preferences</div>
          <label className="block mb-1">
            <input type="checkbox" checked disabled /> Necessary (Always Enabled)
          </label>
          <label className="block mb-1">
            <input
              type="checkbox"
              checked={preferences.analytics}
              onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
            /> Allow anonymous site analytics
          </label>
          <label className="block mb-3">
            <input
              type="checkbox"
              checked={preferences.ads}
              onChange={(e) => setPreferences({ ...preferences, ads: e.target.checked })}
            /> Show personalized ads (helps support the site)
          </label>
          <div className="flex gap-2">
            <button className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded" onClick={() => savePreferences(preferences)}>Save Settings</button>
            <button className="underline text-blue-300" onClick={() => setShowSettings(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
